import React, { useCallback, useEffect, useState } from 'react';
import { Bluetooth, Loader2, QrCode, Save, ShieldCheck, CheckCircle2 } from 'lucide-react';
import mobileDeviceApi from '../services/mobileDeviceApi';
import { useAuth } from '../contexts/AuthContext';

const durations = [30, 60, 120, 240, 480, 720, 1440];

export default function QrPassSettings() {
    const [qrMode, setQrMode] = useState('Static');
    const [devices, setDevices] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [accessMode, setAccessMode] = useState('LOCAL');
    const [ticketMinutes, setTicketMinutes] = useState(60);
    const [qrTransport, setQrTransport] = useState('AUTO');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const { permissions } = useAuth();
    const canManage = permissions.includes('settings:manage') || permissions.includes('mobile_devices:manage_policy');

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [settings, policies] = await Promise.all([
                mobileDeviceApi.getSettings(),
                mobileDeviceApi.getXpassAccessPolicies()
            ]);
            setQrMode(settings.find(s => s.key === 'Mobile.QrPassMode')?.value || 'Static');
            setDevices(Array.isArray(policies) ? policies : []);
        } catch (err) {
            setError(err?.message || 'QR ve Orion geçiş ayarları alınamadı.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const setStaticQr = async () => {
        if (qrMode === 'Static') return;
        setSaving(true);
        setError(null);
        setMessage(null);
        try {
            await mobileDeviceApi.updateSetting('Mobile.QrPassMode', 'Static');
            setQrMode('Static');
            setMessage('Mobil QR kod modeli sabit QR olarak güncellendi.');
        } catch (err) {
            setError(err?.message || 'Ayar güncellenemedi.');
        } finally {
            setSaving(false);
        }
    };

    const loadPolicy = device => {
        setAccessMode(device.accessSecurityMode || 'LOCAL');
        setTicketMinutes(device.secureTicketMinutes || 60);
        setQrTransport(device.qrTransport || 'AUTO');
    };

    const toggleDevice = device => setSelectedIds(current => {
        if (current.includes(device.id)) return current.filter(id => id !== device.id);
        if (!current.length) loadPolicy(device);
        return [...current, device.id];
    });

    const toggleAll = () => {
        if (selectedIds.length === devices.length) return setSelectedIds([]);
        if (devices.length && !selectedIds.length) loadPolicy(devices[0]);
        setSelectedIds(devices.map(d => d.id));
    };

    const savePolicy = async () => {
        if (!selectedIds.length) return setError('Ayar uygulanacak en az bir Orion cihazı seçin.');
        setSaving(true);
        setError(null);
        setMessage(null);
        try {
            const result = await mobileDeviceApi.updateXpassAccessPolicies({
                deviceIds: selectedIds,
                accessSecurityMode: accessMode,
                secureTicketMinutes: Number(ticketMinutes),
                qrTransport
            });
            setDevices(current => current.map(d => selectedIds.includes(d.id)
                ? { ...d, accessSecurityMode: accessMode, secureTicketMinutes: Number(ticketMinutes), qrTransport }
                : d));
            setMessage(`${result?.updated ?? selectedIds.length} cihazın ayarı kaydedildi ve Gateway komut kuyruğuna alındı.`);
        } catch (err) {
            setError(err?.message || 'Orion geçiş ayarları kaydedilemedi.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white dark:bg-[#18191c] p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800">
            <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-500/10 border border-purple-200/70 dark:border-purple-500/20 flex items-center justify-center text-purple-600 dark:text-purple-400 shrink-0">
                    <QrCode className="w-5 h-5" />
                </div>
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-bold text-gray-900 dark:text-white">
                            QR ve Orion Mobil Geçiş Ayarları
                        </h3>
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30">
                            <CheckCircle2 className="w-3 h-3" /> {qrMode === 'Static' ? 'Sabit QR Modu' : 'Dinamik QR'}
                        </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Mobil QR modelini ve Orion/XPass cihazlarının QR/Bluetooth (BLE) çalışma biçimini yönetin.
                    </p>
                </div>
            </div>

            {message && (
                <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 rounded-xl border border-emerald-200 dark:border-emerald-800/50 text-xs font-medium mb-4 flex items-center justify-between">
                    <span>{message}</span>
                    <button type="button" onClick={() => setMessage(null)} className="text-emerald-500 hover:text-emerald-700 font-bold ml-2">✕</button>
                </div>
            )}
            {error && (
                <div className="p-3 bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 rounded-xl border border-rose-200 dark:border-rose-800/50 text-xs font-medium mb-4 flex items-center justify-between">
                    <span>{error}</span>
                    <button type="button" onClick={() => setError(null)} className="text-rose-500 hover:text-rose-700 font-bold ml-2">✕</button>
                </div>
            )}

            <div className="space-y-4">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-4 bg-gray-50/80 dark:bg-[#111214] rounded-xl border border-gray-200/80 dark:border-gray-800">
                    <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-xl bg-purple-100 dark:bg-purple-500/15 text-purple-600 dark:text-purple-400 shrink-0">
                            <QrCode className="w-5 h-5" />
                        </div>
                        <div>
                            <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100">Mobil QR Kod Modeli</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xl leading-relaxed">
                                Sabit QR ekran görüntüsü korumasını kullanır. Dinamik Hikvision QR desteği hazır olduğunda aynı alandan açılacaktır.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center bg-gray-200/80 dark:bg-[#18191c] p-1 rounded-xl border border-gray-200 dark:border-gray-800 w-full md:w-auto">
                        <button
                            type="button"
                            onClick={setStaticQr}
                            disabled={saving || loading || !canManage}
                            className={`px-4 py-2 rounded-lg text-xs font-semibold flex-1 transition-all ${
                                qrMode === 'Static'
                                    ? 'bg-purple-600 text-white shadow-sm'
                                    : 'text-gray-600 dark:text-gray-300'
                            } disabled:opacity-50`}
                        >
                            Sabit QR
                        </button>
                        <button
                            type="button"
                            disabled
                            className="relative px-4 py-2 rounded-lg text-xs font-semibold flex-1 text-gray-500 opacity-50 cursor-not-allowed"
                        >
                            Dinamik QR
                            <span className="absolute -top-2.5 -right-1.5 bg-amber-400 text-amber-950 text-[9px] px-1.5 py-0.5 rounded-full font-bold">
                                YAKINDA
                            </span>
                        </button>
                    </div>
                </div>

                <div className="p-4 bg-gray-50/80 dark:bg-[#111214] rounded-xl border border-gray-200/80 dark:border-gray-800">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
                        <div className="flex items-start gap-3">
                            <div className="p-2.5 rounded-xl bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 shrink-0">
                                <ShieldCheck className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                                    Orion Güvenlik ve QR İletişim Modu
                                </h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Ayarı cihaz bazında veya seçili cihazlara toplu uygulayın. Kayıt sonrası komut ilgili Gateway üzerinden cihaza gönderilir.
                                </p>
                            </div>
                        </div>
                        {devices.length > 0 && (
                            <button
                                type="button"
                                onClick={toggleAll}
                                disabled={loading || !devices.length}
                                className="self-start lg:self-auto px-3 py-1.5 text-xs font-semibold rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#18191c] text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
                            >
                                {selectedIds.length === devices.length && devices.length ? 'Seçimi Temizle' : `Tümünü Seç (${devices.length})`}
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <div className="py-6 flex justify-center items-center text-xs text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Orion cihaz politikaları yükleniyor...
                        </div>
                    ) : !devices.length ? (
                        <div className="p-3.5 rounded-xl bg-gray-100/80 dark:bg-[#18191c] border border-gray-200/70 dark:border-gray-800 text-gray-600 dark:text-gray-400 text-xs flex items-center justify-between">
                            <span>Bu firmada kayıtlı aktif Orion / XPass BLE cihazı bulunmuyor. Cihaz eklendiğinde BLE güvenlik ve QR iletişim ayarları burada listelenir.</span>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 xl:grid-cols-[minmax(280px,1fr)_minmax(420px,1.4fr)] gap-4">
                            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                                {devices.map(device => (
                                    <label
                                        key={device.id}
                                        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                                            selectedIds.includes(device.id)
                                                ? 'border-indigo-500 bg-indigo-50/70 dark:bg-indigo-950/30'
                                                : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-[#18191c]'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(device.id)}
                                            onChange={() => toggleDevice(device)}
                                            className="mt-1 rounded text-indigo-600"
                                        />
                                        <span className="min-w-0">
                                            <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                                {device.deviceName}
                                            </span>
                                            <span className="block text-xs text-gray-500 truncate">
                                                {device.location || 'Konum yok'} · {device.macAddress || device.serialNumber}
                                            </span>
                                            <span className="block text-[11px] text-indigo-600 dark:text-indigo-300 mt-1 font-medium">
                                                {device.accessSecurityMode === 'SECURE' ? `Güvenli · ${device.secureTicketMinutes} dk` : 'Yerel'} · QR: {device.qrTransport}
                                            </span>
                                        </span>
                                    </label>
                                ))}
                            </div>

                            <div className="space-y-4 bg-white dark:bg-[#18191c] p-4 rounded-xl border border-gray-200 dark:border-gray-800">
                                <div>
                                    <label className="block text-xs font-bold text-gray-800 dark:text-gray-100 mb-2">
                                        BLE Geçiş Güvenliği
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {[['LOCAL', 'Yerel / Offline'], ['SECURE', 'Güvenli Kodlu']].map(([value, label]) => (
                                            <button
                                                key={value}
                                                type="button"
                                                onClick={() => setAccessMode(value)}
                                                disabled={!canManage}
                                                className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-colors ${
                                                    accessMode === value
                                                        ? 'bg-indigo-600 border-indigo-600 text-white'
                                                        : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200'
                                                } disabled:opacity-50`}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                                        Yerel modda kayıtlı ve yetkili kişi internet/Gateway olmadan BLE ve kartla geçebilir. Güvenli modda BLE bileti süreli ve sunucu imzalıdır.
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-800 dark:text-gray-100 mb-2">
                                        Güvenli Kod Geçerlilik Süresi
                                    </label>
                                    <select
                                        value={ticketMinutes}
                                        onChange={e => setTicketMinutes(Number(e.target.value))}
                                        disabled={accessMode !== 'SECURE' || !canManage}
                                        className="w-full p-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-[#111214] text-gray-900 dark:text-gray-100 text-xs disabled:opacity-50"
                                    >
                                        {durations.map(value => (
                                            <option key={value} value={value}>
                                                {value < 60 ? `${value} dakika` : `${value / 60} saat`}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-gray-800 dark:text-gray-100 mb-2 flex items-center gap-1.5">
                                        <Bluetooth className="w-4 h-4 text-indigo-500" />
                                        QR Okutulduğunda Kapı Komutu
                                    </label>
                                    <select
                                        value={qrTransport}
                                        onChange={e => setQrTransport(e.target.value)}
                                        disabled={!canManage}
                                        className="w-full p-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-[#111214] text-gray-900 dark:text-gray-100 text-xs disabled:opacity-50"
                                    >
                                        <option value="AUTO">Otomatik — önce servis, ulaşılamazsa BLE</option>
                                        <option value="SERVICE">Yalnızca servis/Gateway üzerinden</option>
                                        <option value="BLE">Doğrudan telefon Bluetooth'u üzerinden</option>
                                    </select>
                                </div>

                                <button
                                    type="button"
                                    onClick={savePolicy}
                                    disabled={saving || !canManage || !selectedIds.length}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    Seçili Cihazlara Uygula ({selectedIds.length})
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
